import React, { useMemo, useCallback, useRef, useEffect } from "react";
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  LineSeries,
  ColumnSeries,
  Tooltip,
  Inject,
  AreaSeries,
  Category,
} from "@syncfusion/ej2-react-charts";
import { formatNumber } from "../../utils/formatNumber";
import "./Chart.css";

/* ---------- helpers ---------- */

function parseHourFromLabel(label) {
  const m = String(label || "").match(/(\d{2})\s*:\s*\d{2}/);
  if (!m) return null;
  const hh = Number(m[1]);
  return Number.isFinite(hh) ? hh : null;
}

function inferDaysCountFromLabels(labels) {
  const days = new Set();
  for (const l of labels || []) {
    const m = String(l || "").match(/^(\d{2}[./-]\d{2})\b/);
    if (m?.[1]) days.add(m[1]);
  }
  return days.size || 1;
}

/* ---------- component ---------- */

export const OverviewUniqueTvChart = ({
  series = [],
  seriesWeekBefore = [],
  showComparison = false,
  chartType = "line",
  title = "Unique TVs",
  loading = false,
  mode = "day",
}) => {

  const chartRef = useRef(null);

  const storedUser = JSON.parse(
    localStorage.getItem("adm_panel_user") || "{}"
  );

  const isDark = !!storedUser.darkmode;

  const axisTextColor = isDark ? "#ffffff" : "#666666";
  const subTitleColor = isDark ? "#aaaaaa" : "#666666";

  const { chartData, chartDataWeekBefore, yAxisMax, labels } = useMemo(() => {
    const clean = (Array.isArray(series) ? series : []).map((p, i) => ({
      x: String(p?.label ?? ""),
      y: Number(p?.value) || 0,
      label: String(p?.label ?? ""),
      idx: i,
    }));

    const cleanWeekBefore = (Array.isArray(seriesWeekBefore) ? seriesWeekBefore : []).map((p, i) => ({
      x: String(p?.label ?? ""),
      y: Number(p?.value) || 0,
      label: String(p?.label ?? ""),
      idx: i,
    }));

    const max = Math.max(
      ...clean.map((d) => d.y),
      ...(showComparison ? cleanWeekBefore.map((d) => d.y) : []),
      0
    );

    return {
      chartData: clean,
      chartDataWeekBefore: cleanWeekBefore,
      labels: clean.map((d) => d.label),
      yAxisMax: max > 0 ? Math.ceil(max * 1.15) : 10,
    };
  }, [series, seriesWeekBefore, showComparison]);

  /* =========================
     ✅ RESIZE OBSERVER FIX
  ========================= */

  useEffect(() => {
    if (!chartRef.current) return;

    const chartInstance = chartRef.current;
    const parentElement = chartInstance.element?.parentElement;

    if (!parentElement) return;

    const resizeObserver = new ResizeObserver(() => {
      chartInstance.refresh();
    });

    resizeObserver.observe(parentElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  /* =========================
     HOURLY STEP
  ========================= */

  const hourStep = useMemo(() => {
    if (mode !== "hour") return 1;
    const daysCount = inferDaysCountFromLabels(labels);
    return daysCount <= 7 ? 6 : 8;
  }, [mode, labels]);

  const showMarkers = mode === "day";

  /* =========================
     AXIS LABEL FILTER
  ========================= */

  const handleAxisLabelRender = useCallback(
    (args) => {
      if (args.axis?.name === "primaryYAxis") {
        const value = parseFloat(args.value);
        if (!isNaN(value)) {
          args.text = formatNumber(value);
        }
        return;
      }

      if (args.axis?.name !== "primaryXAxis") return;

      const label = String(args.text ?? "");

      if (mode === "hour") {
        const hh = parseHourFromLabel(label);
        if (!Number.isFinite(hh) || hh % hourStep !== 0) {
          args.text = "";
          return;
        }
      }

      if (mode === "minute") {
        if (!label.endsWith(":00")) {
          args.text = "";
          return;
        }
      }
    },
    [mode, hourStep]
  );

  /* =========================
     TOOLTIP
  ========================= */

  const handleTooltipRender = useCallback((args) => {
    if (!args.point) return;
    const label = String(args.point.x ?? "");
    args.text = `${label} : ${formatNumber(args.point.y)}`;
  }, []);

  return (
    <>
      <h3 className="chart-title">{title}</h3>
      <p style={{ marginTop: "-8px", marginBottom: "12px", fontSize: "14px", color: subTitleColor }}>
        {showComparison ? "Last 7 Days vs Week Before" : "Last 7 Days"}
      </p>

      {!loading && series?.length > 0 && (
        <ChartComponent
          ref={chartRef}
          width="100%"
          height="420px"
          margin={chartType === "bar" ? { left: 30, right: 40, bottom: 50 } : { left: 15, right: 40, bottom: 50 }}
          legendSettings={{ visible: false }}
          primaryXAxis={{
            name: "primaryXAxis",
            valueType: "Category",
            rangePadding: chartType === "bar" ? "Additional" : "None",
            labelPlacement: "OnTicks",
            labelIntersectAction: "Rotate45",
            majorGridLines: { width: 0 },
            majorTickLines: { width: 0 },
            labelStyle: {
              fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
              color: axisTextColor
            },
          }}
          primaryYAxis={{
            name: "primaryYAxis",
            minimum: 0,
            maximum: yAxisMax,
            majorTickLines: { width: 0 },
            lineStyle: { width: 0 },
            labelFormat: "n0",
            labelStyle: {
              fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
              color: axisTextColor
            },
          }}
          tooltip={{ enable: true }}
          axisLabelRender={handleAxisLabelRender}
          tooltipRender={handleTooltipRender}
        >
          <Inject services={[LineSeries, ColumnSeries, AreaSeries, Category, Tooltip]} />

          <SeriesCollectionDirective>
            <SeriesDirective
              dataSource={chartData}
              xName="x"
              yName="y"
              name="Last 7 Days"
              type={chartType === "bar" ? "Column" : "Line"}
              width={2}
              fill="#1976d2"
              border={{ color: "#1976d2", width: 2 }}
              marker={{
                visible: showMarkers,
                width: 7,
                height: 7,
                fill: "#fff",
                border: { width: 2, color: "#1976d2" },
              }}
              animation={{ enable: false }}
            />

            {showComparison && chartDataWeekBefore.length > 0 && (
              <SeriesDirective
                dataSource={chartDataWeekBefore}
                xName="x"
                yName="y"
                name="Week Before"
                type={chartType === "bar" ? "Column" : "Line"}
                width={2}
                fill="#e91e63"
                border={{ color: "#e91e63", width: 2 }}
                marker={{
                  visible: showMarkers,
                  width: 7,
                  height: 7,
                  fill: "#fff",
                  border: { width: 2, color: "#e91e63" },
                }}
                animation={{ enable: false }}
              />
            )}
          </SeriesCollectionDirective>
        </ChartComponent>
      )}
    </>
  );
};

export default OverviewUniqueTvChart;
